import React, { useState, useEffect, useRef } from 'react';
import { getTenders, deleteTender, getTenderOptions, tenderGoAhead, tenderNoGo, addDiscoveryTask, updateDiscoveryTask, deleteDiscoveryTask, getAssignableUsers } from '../services/api';
import TaskChecklist from './TaskChecklist';
import StagePill from './StagePill';
import { formatDate } from '../utils/formatDate';
import TenderForm from './TenderForm';
import PageHeader from './PageHeader';
import './TenderList.css';

function TenderList({ user, canManage = false }) {
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [options, setOptions] = useState({ sources: [], statuses: [] });
  
  // Modal states
  const [showForm, setShowForm] = useState(false);
  const [editingTender, setEditingTender] = useState(null);
  const [viewingTender, setViewingTender] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [goAheadConfirm, setGoAheadConfirm] = useState(null);
  const [noGoConfirm, setNoGoConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showStagger, setShowStagger] = useState(false);
  const staggerShownRef = useRef(false);
  const [assignableUsers, setAssignableUsers] = useState([]);

  // Fetch options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await getTenderOptions();
        if (response.success) {
          setOptions(response.options);
        }
      } catch (err) {
        console.error('Failed to fetch options:', err);
      }
    };
    const fetchUsers = async () => {
      try {
        const res = await getAssignableUsers();
        if (res.success) setAssignableUsers(res.users || []);
      } catch { /* ignore */ }
    };
    fetchOptions();
    fetchUsers();
  }, []);

  // Fetch tenders
  const fetchTenders = async (overrides = {}) => {
    try {
      setLoading(true);
      const response = await getTenders({
        source: overrides.source !== undefined ? overrides.source : sourceFilter,
        status: overrides.status !== undefined ? overrides.status : statusFilter,
        search: overrides.search !== undefined ? overrides.search : searchTerm,
      });
      if (response.success) {
        setTenders(response.tenders);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceFilter, statusFilter]);

  useEffect(() => {
    if (!loading && tenders.length > 0 && !staggerShownRef.current) {
      staggerShownRef.current = true;
      setShowStagger(true);
    }
  }, [loading, tenders.length]);

  useEffect(() => {
    if (!showStagger) return;
    const t = setTimeout(() => setShowStagger(false), 800);
    return () => clearTimeout(t);
  }, [showStagger]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchTenders();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSourceFilter('');
    setStatusFilter('');
  };

  const hasActiveFilters = searchTerm || sourceFilter || statusFilter;

  // Handle add new tender
  const handleAddNew = () => {
    setEditingTender(null);
    setShowForm(true);
  };

  // Handle edit tender
  const handleEdit = (tender, e) => {
    e?.stopPropagation();
    setEditingTender(tender);
    setShowForm(true);
  };

  // Handle delete tender
  const handleDelete = async (tenderId) => {
    try {
      setActionLoading(true);
      const response = await deleteTender(tenderId);
      if (response.success) {
        setDeleteConfirm(null);
        fetchTenders();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Go Ahead
  const handleGoAhead = async (tenderId) => {
    try {
      setActionLoading(true);
      const response = await tenderGoAhead(tenderId);
      if (response.success) {
        setGoAheadConfirm(null);
        setViewingTender(null);
        fetchTenders();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle No Go
  const handleNoGo = async (tenderId) => {
    try {
      setActionLoading(true);
      const response = await tenderNoGo(tenderId);
      if (response.success) {
        setNoGoConfirm(null);
        setViewingTender(null);
        fetchTenders();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle form success
  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingTender(null);
    fetchTenders();
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Get status badge class
  const getStatusClass = (status) => {
    const statusClasses = {
      discovered: 'status-discovered',
      evaluating: 'status-evaluating',
      participating: 'status-participating',
      dropped: 'status-dropped',
      canceled: 'status-canceled',
      submitted: 'status-submitted',
      won: 'status-won',
      lost: 'status-lost',
      cancelled: 'status-cancelled',
    };
    return statusClasses[status] || 'status-default';
  };

  // Get source badge class
  const getSourceClass = (source) => {
    const sourceClasses = {
      gem: 'source-gem',
      cppp: 'source-cppp',
      state: 'source-state',
      private: 'source-private',
    };
    return sourceClasses[source] || 'source-default';
  };

  // Check if deadline is approaching (within 7 days)
  const isDeadlineApproaching = (dateStr) => {
    if (!dateStr) return false;
    const deadline = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 7;
  };

  // Check if deadline has passed
  const isDeadlinePassed = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  return (
    <div className="tender-list-container">
      <PageHeader
        className="tender-list-header"
        title="Tender Discovery"
        subtitle="Track and manage discovered tenders from various sources"
        actions={canManage && (
          <button className="add-btn" onClick={handleAddNew}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add Tender
          </button>
        )}
      />

      {/* Filters */}
      <div className="filters-section">
        <form onSubmit={handleSearch} className="search-box">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            type="text"
            placeholder="Search by reference, title, or buyer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button type="submit">Search</button>
        </form>
        
        <div className="filter-dropdowns">
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <option value="">All Sources</option>
            {options.sources?.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            {options.statuses?.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          {hasActiveFilters && (
            <button type="button" className="btn-secondary" onClick={clearFilters} style={{ marginLeft: 8 }}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Loading State - skeleton table */}
      {loading ? (
        <div className="tenders-table-wrapper">
          <table className="tenders-table table-skeleton">
            <thead>
              <tr>
                <th style={{ width: '32px' }}></th>
                <th>Reference</th>
                <th>Title</th>
                <th>Source</th>
                <th>Buyer / Department</th>
                <th>Deadline</th>
                <th>Est. Value</th>
                <th>Products</th>
                <th>Status</th>
                {canManage && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5].map((i) => (
                <tr key={i}>
                  <td><span className="skeleton-box" style={{ width: 20 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 100 }} /></td>
                  <td><span className="skeleton-box" style={{ width: '50%' }} /></td>
                  <td><span className="skeleton-box" style={{ width: 72 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 100 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 88 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 70 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 52 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 80 }} /></td>
                  {canManage && <td><span className="skeleton-box" style={{ width: 90 }} /></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : tenders.length === 0 ? (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
          </svg>
          <h3>No tenders found</h3>
          <p>{searchTerm || sourceFilter || statusFilter ? 'Try different filters' : 'Start by adding your first tender'}</p>
          {canManage && !searchTerm && !sourceFilter && !statusFilter && (
            <button onClick={handleAddNew}>Add Tender</button>
          )}
        </div>
      ) : (
        /* Tenders Table */
        <div className="tenders-table-wrapper">
          <table className="tenders-table">
            <thead>
              <tr>
                <th style={{ width: '32px' }}></th>
                <th>Reference</th>
                <th>Title</th>
                <th>Source</th>
                <th>Buyer / Department</th>
                <th>Deadline</th>
                <th>Est. Value</th>
                <th>Products</th>
                <th>Status</th>
                {canManage && <th>Actions</th>}
              </tr>
            </thead>
            <tbody className={showStagger ? 'stagger-rows' : ''}>
              {tenders.map((tender, index) => (
                <React.Fragment key={tender.id}>
                  <tr
                    style={showStagger ? { animationDelay: `${index * 0.03}s` } : undefined}
                    onClick={() => setExpandedId(expandedId === tender.id ? null : tender.id)}
                    className={`tender-row tender-row-clickable ${isDeadlinePassed(tender.submission_deadline) ? 'deadline-passed' : ''} ${expandedId === tender.id ? 'expanded' : ''}`}
                  >
                    <td className="expand-cell">
                      <svg className={`expand-icon ${expandedId === tender.id ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="ref-code ref-code-link"
                        onClick={(e) => { e.stopPropagation(); setViewingTender(tender); }}
                        title="View tender details"
                      >
                        {tender.tender_ref}
                      </button>
                    </td>
                    <td><span className="tender-title-inline">{tender.title || '–'}</span></td>
                    <td>
                      <span className={`source-badge ${getSourceClass(tender.source)}`}>
                        {tender.source_display}
                      </span>
                    </td>
                    <td>{tender.buyer_name || '-'}</td>
                    <td>
                      <span className={`deadline ${isDeadlineApproaching(tender.submission_deadline) ? 'approaching' : ''} ${isDeadlinePassed(tender.submission_deadline) ? 'passed' : ''}`}>
                        {formatDate(tender.submission_deadline)}
                        {isDeadlineApproaching(tender.submission_deadline) && !isDeadlinePassed(tender.submission_deadline) && (
                          <span className="deadline-warning">Soon!</span>
                        )}
                      </span>
                    </td>
                    <td>{formatCurrency(tender.tender_value)}</td>
                    <td>
                      <span className="product-count">{(tender.products && tender.products.length) || 0} items</span>
                    </td>
                    <td>
                      <StagePill status={tender.status} />
                    </td>
                    {canManage && (
                      <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                        <button className="go-ahead-btn" onClick={(e) => { e.stopPropagation(); setGoAheadConfirm(tender); }} title="Go Ahead">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg>
                        </button>
                        <button className="no-go-btn" onClick={(e) => { e.stopPropagation(); setNoGoConfirm(tender); }} title="No Go">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </button>
                        <button className="edit-btn" onClick={(e) => handleEdit(tender, e)} title="Edit">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                        </button>
                        <button className="delete-btn" onClick={(e) => { e.stopPropagation(); setDeleteConfirm(tender); }} title="Delete">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                        </button>
                        <button type="button" className="edit-btn" onClick={(e) => { e.stopPropagation(); setViewingTender(tender); }} title="View full details">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                        </button>
                      </td>
                    )}
                  </tr>
                  {expandedId === tender.id && (
                    <tr className="tender-detail-row">
                      <td colSpan={canManage ? 10 : 9}>
                        <div className="tender-expanded-details">
                          <div className="tender-details-grid">
                            <div className="detail-item"><label>Buyer / Department</label><span>{tender.buyer_name || '-'}</span></div>
                            <div className="detail-item"><label>Publish Date</label><span>{formatDate(tender.publish_date)}</span></div>
                            <div className="detail-item"><label>Submission Deadline</label><span>{formatDate(tender.submission_deadline)}</span></div>
                            <div className="detail-item"><label>EMD Amount</label><span>{formatCurrency(tender.emd_amount)}</span></div>
                            <div className="detail-item"><label>Estimated Value</label><span>{formatCurrency(tender.tender_value)}</span></div>
                            <div className="detail-item"><label>Products</label><span>{(tender.products && tender.products.length) || 0} items</span></div>
                          </div>
                          {tender.description && <p className="tender-description-snippet">{tender.description}</p>}
                          <TaskChecklist
                            tasks={tender.tasks || []}
                            canManage={canManage}
                            users={assignableUsers}
                            onAdd={async (taskData) => {
                              try {
                                await addDiscoveryTask(tender.id, taskData);
                                fetchTenders();
                              } catch (err) { alert(err.message); }
                            }}
                            onUpdate={async (taskId, data) => {
                              try {
                                await updateDiscoveryTask(tender.id, taskId, data);
                                fetchTenders();
                              } catch (err) { alert(err.message); }
                            }}
                            onDelete={async (taskId) => {
                              try {
                                await deleteDiscoveryTask(tender.id, taskId);
                                fetchTenders();
                              } catch (err) { alert(err.message); }
                            }}
                          />
                          <button type="button" className="link-btn" onClick={(e) => { e.stopPropagation(); setViewingTender(tender); }}>View full details →</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Total Count */}
      {!loading && tenders.length > 0 && (
        <div className="list-footer">
          <span>Total: {tenders.length} tenders</span>
        </div>
      )}

      {/* Tender Form Modal */}
      {showForm && (
        <TenderForm
          tender={editingTender}
          options={options}
          onSuccess={handleFormSuccess}
          onClose={() => {
            setShowForm(false);
            setEditingTender(null);
          }}
        />
      )}

      {/* Tender View Modal */}
      {viewingTender && (
        <div className="modal-overlay" onClick={() => setViewingTender(null)}>
          <div className="tender-view-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setViewingTender(null)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            
            <div className="tender-view-content">
              <div className="tender-view-header">
                <span className={`source-badge ${getSourceClass(viewingTender.source)}`}>
                  {viewingTender.source_display}
                </span>
                <span className={`status-badge ${getStatusClass(viewingTender.status)}`}>
                  {viewingTender.status_display}
                </span>
              </div>
              
              <h2>{viewingTender.tender_ref}</h2>
              {viewingTender.title && <p className="tender-title-view">{viewingTender.title}</p>}
              
              <div className="tender-details-grid">
                <div className="detail-item">
                  <label>Buyer / Department</label>
                  <span>{viewingTender.buyer_name || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Publish Date</label>
                  <span>{formatDate(viewingTender.publish_date)}</span>
                </div>
                <div className="detail-item">
                  <label>Submission Deadline</label>
                  <span className={isDeadlinePassed(viewingTender.submission_deadline) ? 'text-danger' : ''}>
                    {formatDate(viewingTender.submission_deadline)}
                  </span>
                </div>
                <div className="detail-item">
                  <label>EMD Amount</label>
                  <span>{formatCurrency(viewingTender.emd_amount)}</span>
                </div>
                <div className="detail-item">
                  <label>Estimated Value</label>
                  <span className="value-highlight">{formatCurrency(viewingTender.tender_value)}</span>
                </div>
                <div className="detail-item">
                  <label>Products</label>
                  <span>{(viewingTender.products && viewingTender.products.length) || 0} items</span>
                </div>
              </div>
              
              {viewingTender.portal_url && (
                <a 
                  href={viewingTender.portal_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="portal-link"
                >
                  View on Portal →
                </a>
              )}
            </div>
            
            {canManage && (
              <div className="tender-view-actions">
                <button 
                  className="go-ahead-btn-large"
                  onClick={() => setGoAheadConfirm(viewingTender)}
                >
                  Go Ahead
                </button>
                <button 
                  className="no-go-btn-large"
                  onClick={() => setNoGoConfirm(viewingTender)}
                >
                  No Go
                </button>
                <button 
                  className="edit-btn-large"
                  onClick={() => {
                    setViewingTender(null);
                    handleEdit(viewingTender);
                  }}
                >
                  Edit
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="confirm-modal">
            <div className="confirm-icon delete">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </div>
            <h3>Delete Tender</h3>
            <p>Are you sure you want to delete <strong>{deleteConfirm.tender_ref}</strong>?</p>
            <p className="warning">This action cannot be undone.</p>
            <div className="modal-actions">
              <button 
                className="cancel-btn" 
                onClick={() => setDeleteConfirm(null)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn" 
                onClick={() => handleDelete(deleteConfirm.id)}
                disabled={actionLoading}
              >
                {actionLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Go Ahead Confirmation Modal */}
      {goAheadConfirm && (
        <div className="modal-overlay">
          <div className="confirm-modal">
            <div className="confirm-icon go-ahead">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h3>Confirm Go Ahead</h3>
            <p>Move <strong>{goAheadConfirm.tender_ref}</strong> to Participating list?</p>
            <p className="info">This tender will be moved to the participating tenders section.</p>
            <div className="modal-actions">
              <button 
                className="cancel-btn" 
                onClick={() => setGoAheadConfirm(null)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button 
                className="confirm-go-ahead-btn" 
                onClick={() => handleGoAhead(goAheadConfirm.id)}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : 'Confirm Go Ahead'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No Go Confirmation Modal */}
      {noGoConfirm && (
        <div className="modal-overlay">
          <div className="confirm-modal">
            <div className="confirm-icon no-go">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </div>
            <h3>Confirm No Go</h3>
            <p>Mark <strong>{noGoConfirm.tender_ref}</strong> as No Go?</p>
            <p className="warning">This tender will be moved to the No Go list.</p>
            <div className="modal-actions">
              <button 
                className="cancel-btn" 
                onClick={() => setNoGoConfirm(null)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button 
                className="confirm-no-go-btn" 
                onClick={() => handleNoGo(noGoConfirm.id)}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : 'Confirm No Go'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TenderList;
